import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../../api";
import { formatDateTime } from "../../../shared/time";
import { Badge, ErrorNote } from "../../ui";
import { useSession } from "../../shell/session";
import { CapacityMeter, PageFrame, hasRole, messageOf } from "../calendar/chrome";
import type { RegistrationRecord } from "./types";

export function RegistrationAdminPage() {
  const { registrationId = "" } = useParams();
  const { user } = useSession();
  const canEdit = hasRole(user?.role, "coordinator");
  const [registration, setRegistration] = useState<RegistrationRecord | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [pending, setPending] = useState("");
  const [saving, setSaving] = useState("");

  useEffect(() => {
    let live = true;
    void api<RegistrationRecord>(`/api/v1/registrations/${registrationId}`)
      .then((row) => { if (live) setRegistration(row); })
      .catch((caught: unknown) => { if (live) setError(messageOf(caught)); });
    return () => { live = false; };
  }, [registrationId]);

  async function cancel(registrantId: string) {
    setSaving(registrantId);
    setError("");
    try {
      setRegistration(await api<RegistrationRecord>(`/api/v1/registrations/${registrationId}/cancel/${registrantId}`, { method: "POST" }));
      setPending("");
      setNotice("Signup cancelled. Confirmed places are back in the count.");
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setSaving("");
    }
  }

  async function copyLink(slug: string) {
    const link = `${window.location.origin}/r/${slug}`;
    try {
      await navigator.clipboard.writeText(link);
      setNotice("Copied the public link.");
    } catch {
      setNotice("Clipboard is blocked here. Select the link and copy it.");
    }
  }

  const people = registration?.registrants ?? [];

  return (
    <PageFrame
      kicker="Signup"
      title={registration?.title ?? "Signup"}
      lede={registration ? `${formatDateTime(registration.startsAt)} · ${registration.place || "Place not set"}` : undefined}
      actions={<Link className="btn ghost" to="/registrations">All signups</Link>}
    >
      {error && <ErrorNote>{error}</ErrorNote>}
      {notice && <p className="ok-note" role="status">{notice}</p>}
      {!registration && !error ? <p>Loading the room…</p> : null}
      {registration && (
        <div className="grid-2">
          <article className="panel panel-pad stack">
            <CapacityMeter confirmed={registration.confirmedCount} remaining={registration.remaining} capacity={registration.capacity} />
            <div className="cluster">
              <Badge tone={registration.published ? "teal" : "amber"}>{registration.published ? "Published" : "Not published"}</Badge>
              <Badge tone={registration.allowWaitlist ? "violet" : "mute"}>{registration.allowWaitlist ? "Waitlist on" : "Waitlist off"}</Badge>
            </div>
            <p className="prose">{registration.description || "No description on the event."}</p>
            <p className="muted">{registration.confirmationNote || "No confirmation note."}</p>
            <p className="faint">Capacity, the public link, published state, and waitlist were set when this signup opened. The API does not accept later edits. Cancel a confirmed person to release seats.</p>
            <label className="field">
              Public page
              <input readOnly value={`${window.location.origin}/r/${registration.publicSlug}`} />
            </label>
            <div className="cluster">
              <button className="btn" type="button" onClick={() => void copyLink(registration.publicSlug)}>Copy public link</button>
              {registration.published ? <Link className="btn teal" to={`/r/${registration.publicSlug}`}>Open public page</Link> : <p className="notice">The public page returns not found until a signup is created as published. This one cannot be published later.</p>}
            </div>
          </article>
          <article className="panel table-wrap">
            <div className="panel-pad"><h2>Signups</h2></div>
            {people.length === 0 ? <div className="panel-pad"><p className="muted">Nobody has signed up.</p></div> : (
              <table className="data">
                <thead><tr><th>Name</th><th>Email</th><th>Party</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {people.map((person) => (
                    <tr key={person.id}>
                      <td>{person.name}</td>
                      <td>{person.email}</td>
                      <td>{person.partySize}</td>
                      <td><Badge tone={person.status === "confirmed" ? "teal" : person.status === "waitlist" ? "amber" : "mute"}>{person.status}</Badge></td>
                      <td>
                        {person.status === "cancelled" || !canEdit ? null : pending === person.id ? (
                          <span className="cluster">
                            <button className="btn small danger" type="button" disabled={saving === person.id} onClick={() => void cancel(person.id)}>{saving === person.id ? "Saving…" : "Release place"}</button>
                            <button className="btn small ghost" type="button" onClick={() => setPending("")}>Keep</button>
                          </span>
                        ) : (
                          <button className="btn small ghost" type="button" onClick={() => setPending(person.id)}>Cancel signup</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {!canEdit && <p className="panel-pad notice">Cancelling a signup needs a coordinator login.</p>}
          </article>
        </div>
      )}
    </PageFrame>
  );
}
